/* eslint-disable @typescript-eslint/indent */
/* eslint-disable @typescript-eslint/no-shadow */
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { isEmpty, isNaN, isNil, isNumber, toNumber } from "lodash";
import { AnyObject, SelectProperty, QueryParamsTable } from "../../types/app.type";
import { setValueToDeeperProperty } from "../../utils/objects/object";
import { ConditionFilter, SearchFilterComponent, SearchFilterOption } from "./search-filter/search-filter.component";
import { SearchInputComponent } from "./search-input/search-input.component";

export type FilterType = { [key in ConditionFilter]?: string | number } | string | number | boolean;

export class SearchEvent {
  filter: string;
  value: string;
  auxFilter: string;
  auxValue: string;
  constructor(filter: string, value: string, auxFilter?: string, auxValue?: string) {
    this.filter = filter;
    this.value = value;
    this.auxFilter = auxFilter;
    this.auxValue = auxValue;
  }
}

@Component({
  selector: "app-search-bar",
  templateUrl: "./search-bar.component.html",
  styleUrls: ["./search-bar.component.scss"],
})
export class SearchBarComponent implements OnInit, AfterViewInit, AfterContentChecked {
  @Input() alias: string;
  @Input() disabled: boolean;
  @Input() filterHidden: boolean;
  @Input() iconHidden: boolean;
  @Input() syncWithQueryParams: boolean;
  @Input() searching: boolean;
  @Input() options: SearchFilterOption[];
  @Input() defaultOption: string;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() searchEvent = new EventEmitter<SearchEvent>();
  @Output() asyncSearchEvent = new EventEmitter<SearchEvent>();
  @ViewChild("searchFilter") searchFilter: SearchFilterComponent;
  @ViewChild("searchInput") searchInput: SearchInputComponent;
  inputDisabled = true;

  constructor(private router: Router, private route: ActivatedRoute, private changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.options = this.options.filter((o) => !o.hidden);
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  ngAfterViewInit(): void {
    if (this.defaultOption) {
      this.searchFilter.selectedOption = this.options.find((o) => o.value === this.defaultOption);
    }
    this.mapQueryParamsToSearchBar();
  }

  // #region functions
  getFilterParam(): string {
    return this.alias + "_filter";
  }

  getValueParam(): string {
    return this.alias + "_value";
  }

  getAuxFilterParam(): string {
    return this.alias + "_auxFilter";
  }

  getAuxValueParam(): string {
    return this.alias + "_auxValue";
  }

  getValues(): AnyObject | AnyObject[] {
    const {
      value: filter,
      auxValue: auxFilter,
      filterByProperty,
      filterByFunction,
    } = this.searchFilter?.selectedOption || {};
    const { value, auxValue } = this.searchInput || {};

    function getValue(
      searchBar: SearchBarComponent,
      value: string,
      filter: string,
      filterByProperty?: SelectProperty<any> | SelectProperty<any>[],
      filterByFunction?: (value: FilterType) => AnyObject | AnyObject[],
    ): AnyObject {
      const _value = filterByFunction
        ? value
        : searchBar.setFilterCondition(searchBar.searchFilter.selectedOption, value);

      if (isNil(filter) || _value === null || _value === undefined) {
        return {};
      }

      if (filterByProperty) {
        if (Array.isArray(filterByProperty)) {
          return filterByProperty.map((prop) => setValueToDeeperProperty(_value, prop));
        }
        return setValueToDeeperProperty(_value, filterByProperty);
      }

      if (filterByFunction) {
        return filterByFunction(_value);
      }

      return { [filter]: _value };
    }

    const mergedValues = {
      ...getValue(this, value, filter, filterByProperty, filterByFunction),
      ...getValue(this, auxValue, auxFilter, filterByProperty, filterByFunction),
    };

    let values: AnyObject | AnyObject[] = [];

    if (!isEmpty(mergedValues)) {
      const hasMultipleFilters = Object.keys(mergedValues).length > 1;
      // If there are multiple filters we have to set each filter one by one
      // to the "where" object/array
      if (hasMultipleFilters) {
        Object.keys(mergedValues).forEach((key) => {
          // Here we check if key is a integer due a previous array convertion (to object)
          if (Number.isInteger(parseInt(key, 10))) {
            values.push(mergedValues[key]);
          } else {
            values = { ...values, [key]: mergedValues[key] };
          }
        });
      } else {
        values = mergedValues;
      }
    }

    return values;
  }

  getQueryParams(): Params {
    const selectedOptionValue = !!this.searchFilter?.selectedOption?.value;
    const searchInputValue = isNumber(this.searchInput?.value) || this.searchInput?.value ? true : false;

    return {
      ...((selectedOptionValue &&
        searchInputValue && {
          [this.getFilterParam()]: this.searchFilter?.selectedOption?.value,
          [this.getValueParam()]: this.searchInput?.value,
        }) || {
        [this.getFilterParam()]: undefined,
        [this.getValueParam()]: undefined,
      }),
      ...((this.searchFilter?.selectedOption?.auxValue &&
        this.searchInput?.auxValue && {
          [this.getAuxFilterParam()]: this.searchFilter?.selectedOption?.auxValue,
          [this.getAuxValueParam()]: this.searchInput?.auxValue,
        }) || {
        [this.getAuxFilterParam()]: undefined,
        [this.getAuxValueParam()]: undefined,
      }),
    };
  }

  clear(emitChange = true): void {
    this.searchFilter.selectedOption = null;
    this.searchInput.value = null;
    this.searchInput.auxValue = null;
    this.updateQueryParams(undefined, undefined, undefined, undefined);
    if (emitChange) {
      this.searchFilter.changeSelection();
    }
  }

  changeFilter(selectedFilter: SearchFilterOption): void {
    this.searchFilter.selectedOption = selectedFilter;

    // do search when filter doesn't allow input value.
    this.inputDisabled = !selectedFilter || selectedFilter.inputDisabled;
    this.searchInput.inMemoryOptions = [];
    if (this.inputDisabled) {
      this.searchInput.value = undefined;
      this.search(this.searchInput.value);
    }
  }

  search(value: string): void {
    if (
      !this.searchFilter?.selectedOption?.minLength ||
      value?.length === 0 ||
      value?.length >= (this.searchFilter?.selectedOption?.minLength || 0) ||
      value === undefined
    ) {
      setTimeout(() => {
        const filter = this.searchFilter?.selectedOption;
        const value = this.searchInput?.value;
        const auxValue =
          this.searchInput?.options?.find((opt) => opt.value === value)?.auxValue ||
          this.searchInput?.inMemoryOptions?.find((opt) => opt.value === value)?.auxValue;

        this.updateQueryParams(filter?.value, value, filter?.auxValue, auxValue);
        this.searchEvent.emit(new SearchEvent(filter?.value, value, filter?.auxValue, auxValue));
      });
    }
  }

  asyncSearch(value: string): void {
    if (
      !this.searchFilter?.selectedOption?.minLength ||
      value?.length === 0 ||
      value?.length >= (this.searchFilter?.selectedOption?.minLength || 0)
    ) {
      setTimeout(() => {
        const filter = this.searchFilter?.selectedOption;
        const value = this.searchInput?.value;
        const auxValue =
          this.searchInput?.options?.find((opt) => opt.value === value)?.auxValue ||
          this.searchInput?.inMemoryOptions?.find((opt) => opt.value === value)?.auxValue;

        this.updateQueryParams(filter?.value, value, filter?.auxValue, auxValue);

        this.asyncSearchEvent.emit(new SearchEvent(filter?.value, value, filter?.auxValue, auxValue));
      });
    }
  }
  // #endregion

  // #region private functions

  private setFilterCondition(searchFilterOption: SearchFilterOption, searchInputValue: string | number): FilterType {
    if (searchInputValue === undefined || searchInputValue === null || searchInputValue === "") {
      return null;
    }

    if (searchFilterOption?.condition) {
      if (searchFilterOption.condition === "$eq") {
        return searchInputValue;
      }
      return { [searchFilterOption.condition]: searchInputValue };
    }
    return { $cont: searchInputValue }; // default filter condition is "contains"
  }

  private paramsToObject(entries) {
    const result = {};
    for (const [key, value] of entries) {
      const valueToNumber = toNumber(value);
      result[key] = isNaN(valueToNumber) ? value : valueToNumber;
    }
    return result;
  }

  private mapQueryParamsToSearchBar(): void {
    if (!this.syncWithQueryParams || !location.search) {
      return;
    }

    const queryParams = new URLSearchParams(location.search);

    const values = this.paramsToObject(queryParams.entries());

    const filter = values[this.getFilterParam()] || undefined;
    const auxFilter = values[this.getAuxFilterParam()] || undefined;

    const value = values[this.getValueParam()];
    const validValue = value !== false && value !== undefined;

    const auxValue = values[this.getAuxValueParam()];
    const validAuxValue = auxValue !== false && auxValue !== undefined;

    // Sync filter
    if ((filter || auxFilter) && (validValue || validAuxValue)) {
      this.searchFilter.selectedOption = this.options.find(
        (opt) => (opt.value && opt.value === filter) || (opt.auxValue && opt.auxValue === auxFilter),
      );
    }
    // Sync input
    if (this.searchFilter?.selectedOption?.value) {
      this.searchInput.value = value;
      this.searchInput.auxValue = auxValue;
    }

    if (this.searchFilter?.selectedOption?.value) {
      this.inputDisabled = false;
    }
  }

  private updateQueryParams(filter: string, value: string, auxFilter?: string, auxValue?: string): void {
    if (!this.syncWithQueryParams) {
      return;
    }

    const queryParams: QueryParamsTable = {
      [this.getFilterParam()]: filter,
      [this.getValueParam()]: value,
      [this.getAuxFilterParam()]: auxFilter,
      [this.getAuxValueParam()]: auxValue,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: "merge",
    });
  }
  // #endregion
}
